import { Formik, Form } from "formik";
import { Checkbox, Input, Button, Modal, Alert } from "antd";
import Link from "next/link";
import * as yup from "yup";
import FormItem from "components/form-item";
import { useRouter } from "next/router";
import { useUser } from "hooks/useAuth";
import { useCreateUserMutation } from "generated/graphql";
import fromErrors from "utils/from-errors";
import { InfoCircleOutlined } from "@ant-design/icons";
import React from "react";
import PasswordGuidance from "components/password-guidance";
import { useDeadline } from "context/deadline-context";

const schema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().required("Email is required"),
  applicationName: yup.string().required("Application name is required"),
  password: yup.string().required("Password is required"),
  confirmation: yup
    .boolean()
    .oneOf([true], "You need to agree")
    .required("You need to agree"),
});

export default function Register() {
  const { refresh } = useUser({
    redirectTo: "/",
    redirectIfFound: true,
  });
  const { deadlineExpired } = useDeadline();
  const [visible, setVisible] = React.useState(false);
  const [createUser] = useCreateUserMutation();
  const router = useRouter();

  return (
    <div className="mx-auto max-w-xl">
      {deadlineExpired && (
        <Alert message="Application deadline expired" type="warning" showIcon />
      )}
      <div className="bg-white rounded shadow-xl p-4 mt-4 self-start">
        <div className="max-w-xl">
          <h1 className="text-blue-600">Register</h1>
          <Formik
            validationSchema={schema}
            initialValues={{
              firstName: "",
              lastName: "",
              email: "",
              applicationName: "",
              password: "",
              confirmPassword: "",
              confirmation: undefined,
            }}
            onSubmit={(values, { setErrors }) => {
              createUser({
                variables: {
                  input: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    applicationName: values.applicationName,
                    password: values.password,
                    confirmPassword: values.confirmPassword,
                  },
                },
              }).then((res) => {
                setErrors(fromErrors(res?.data?.createUser?.errors));
                if (!res?.data?.createUser?.errors?.length) {
                  localStorage.setItem(
                    "auth_token",
                    res?.data?.createUser?.token
                  );
                  refresh().then(() => {
                    if (res?.data?.createUser?.activationKey) {
                      localStorage.setItem(
                        "activationKey",
                        res?.data?.createUser?.activationKey
                      );
                    }
                    if (res?.data?.createUser?.registration) {
                      router.push("/");
                    }
                  });
                }
              });
            }}
          >
            <Form>
              <FormItem
                name="firstName"
                label="First Name"
                disabled={deadlineExpired}
              />
              <FormItem
                name="lastName"
                label="Last Name"
                disabled={deadlineExpired}
              />
              <FormItem name="email" label="Email" disabled={deadlineExpired} />
              <FormItem
                name="applicationName"
                label="Application name*"
                note={
                  <p>
                    *Application name is the name that we will use to identify
                    your application. We will use this name in our
                    communications with you about your application. We recommend
                    that your application name represents your organisation e.g.
                    company name, trading name or provider name.
                  </p>
                }
                disabled={deadlineExpired}
              />
              <FormItem
                name="password"
                label={
                  <div>
                    Password
                    <InfoCircleOutlined
                      onClick={() => {
                        setVisible(true);
                      }}
                      className="ml-2"
                    />
                    <PasswordGuidance
                      visible={visible}
                      setVisible={setVisible}
                    />
                  </div>
                }
                as={Input.Password}
                disabled={deadlineExpired}
              />
              <FormItem
                name="confirmPassword"
                label="Confirm password"
                as={Input.Password}
                disabled={deadlineExpired}
              />
              <FormItem
                name="confirmation"
                as={Checkbox}
                type="checkbox"
                disabled={deadlineExpired}
              >
                I agree to the CarePulse{" "}
                <a href="/terms_of_use" target="_blank">
                  Terms of Use
                </a>
                ,{" "}
                <a href="privacy_policy" target="_blank">
                  Privacy Notice
                </a>{" "}
                and to the use of{" "}
                <a href="privacy_policy" target="_blank">
                  Cookies
                </a>
                .
              </FormItem>
              <Button
                type="primary"
                htmlType="submit"
                data-testid="register"
                disabled={deadlineExpired}
              >
                Register
              </Button>
              <div className="mt-4">
                Already registered?
                <Link href="/login">
                  <a> Login here</a>
                </Link>
              </div>
            </Form>
          </Formik>
        </div>
      </div>
    </div>
  );
}
